const VARIABLE_REGEX = /\{\{(\d+)\}\}/g;

export const extractVariables = text =>
  [...(text || '').matchAll(VARIABLE_REGEX)]
    .map(match => match[1])
    .filter((value, index, list) => list.indexOf(value) === index)
    .sort((a, b) => Number(a) - Number(b));

const MEDIA_HEADER_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

const buildHeaderComponent = form => {
  if (MEDIA_HEADER_FORMATS.includes(form.headerFormat)) {
    return {
      type: 'HEADER',
      format: form.headerFormat,
      example: { header_handle: [form.headerHandle || ''] },
    };
  }
  if (!form.headerText || form.headerFormat === 'none') return null;

  const component = { type: 'HEADER', format: 'TEXT', text: form.headerText };
  const variables = extractVariables(form.headerText);
  if (variables.length) {
    component.example = {
      header_text: variables.map(key => form.headerExamples?.[key] || ''),
    };
  }
  return component;
};

const buildBodyComponent = form => {
  const component = { type: 'BODY', text: form.body };
  const variables = extractVariables(form.body);
  if (variables.length) {
    component.example = {
      body_text: [variables.map(key => form.bodyExamples?.[key] || '')],
    };
  }
  return component;
};

const buildButton = button => {
  switch (button.type) {
    case 'URL': {
      const built = { type: 'URL', text: button.text, url: button.url };
      if (extractVariables(button.url).length) {
        built.example = [button.urlExample || ''];
      }
      return built;
    }
    case 'PHONE_NUMBER':
      return {
        type: 'PHONE_NUMBER',
        text: button.text,
        phone_number: button.phoneNumber,
      };
    case 'COPY_CODE':
      return { type: 'COPY_CODE', example: button.example };
    default:
      return { type: 'QUICK_REPLY', text: button.text };
  }
};

const buildAuthenticationComponents = form => {
  const components = [
    {
      type: 'BODY',
      add_security_recommendation: !!form.addSecurityRecommendation,
    },
  ];
  if (form.codeExpirationMinutes) {
    components.push({
      type: 'FOOTER',
      code_expiration_minutes: Number(form.codeExpirationMinutes),
    });
  }
  components.push({
    type: 'BUTTONS',
    buttons: [{ type: 'OTP', otp_type: 'COPY_CODE' }],
  });
  return components;
};

export const buildTemplatePayload = form => {
  if (form.category === 'AUTHENTICATION') {
    return {
      name: form.name,
      category: form.category,
      language: form.language,
      components: buildAuthenticationComponents(form),
    };
  }

  const components = [];
  const header = buildHeaderComponent(form);
  if (header) components.push(header);
  components.push(buildBodyComponent(form));
  if (form.footer) components.push({ type: 'FOOTER', text: form.footer });
  if (form.buttons?.length) {
    components.push({
      type: 'BUTTONS',
      buttons: form.buttons.map(buildButton),
    });
  }

  return {
    name: form.name,
    category: form.category,
    language: form.language,
    components,
  };
};

export const slugifyTemplateName = value =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9_\s]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 512);

const exampleByVariable = (text, examples) =>
  Object.fromEntries(
    extractVariables(text).map((key, index) => [key, examples?.[index] || ''])
  );

export const emptyTemplateForm = () => ({
  name: '',
  category: 'MARKETING',
  language: 'pt_BR',
  headerFormat: 'none',
  headerText: '',
  headerExamples: {},
  headerHandle: '',
  body: '',
  bodyExamples: {},
  footer: '',
  buttons: [],
  addSecurityRecommendation: true,
  codeExpirationMinutes: 10,
});

const buttonFormFromMeta = button => ({
  type: button.type,
  text: button.text || '',
  url: button.url || '',
  urlExample: button.example?.[0] || '',
  phoneNumber: button.phone_number || '',
  example: button.type === 'COPY_CODE' ? button.example || '' : '',
});

export const templateFormFromMeta = template => {
  const form = {
    ...emptyTemplateForm(),
    name: template.name,
    category: template.category,
    language: template.language,
  };
  const find = type => template.components?.find(c => c.type === type);

  const header = find('HEADER');
  if (header?.format === 'TEXT') {
    form.headerFormat = 'TEXT';
    form.headerText = header.text || '';
    form.headerExamples = exampleByVariable(
      header.text,
      header.example?.header_text
    );
  } else if (MEDIA_HEADER_FORMATS.includes(header?.format)) {
    form.headerFormat = header.format;
    form.headerHandle = header.example?.header_handle?.[0] || '';
  }

  const body = find('BODY');
  if (template.category === 'AUTHENTICATION') {
    form.addSecurityRecommendation = !!body?.add_security_recommendation;
    form.codeExpirationMinutes = find('FOOTER')?.code_expiration_minutes || 10;
    return form;
  }
  form.body = body?.text || '';
  form.bodyExamples = exampleByVariable(
    body?.text,
    body?.example?.body_text?.[0]
  );
  form.footer = find('FOOTER')?.text || '';
  form.buttons = (find('BUTTONS')?.buttons || []).map(buttonFormFromMeta);
  return form;
};

// Renumera {{n}} pela ordem de aparição e leva os exemplos junto.
export const renumberVariables = (text, examples = {}) => {
  const order = [];
  const next = (text || '').replace(VARIABLE_REGEX, (_, key) => {
    if (!order.includes(key)) order.push(key);
    return `{{${order.indexOf(key) + 1}}}`;
  });
  const renumbered = Object.fromEntries(
    order.map((key, index) => [String(index + 1), examples[key] || ''])
  );
  return { text: next, examples: renumbered };
};
